import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { isContractActive } from "../utils/commonMethods";
import { getAccessToken } from "../utils/axios";
import { useAuth } from "../../lib/auth/AuthContext";
import { BaseClient, type ICropwiseBaseContract } from "../utils/BaseClient";

export interface IPlanInfo {
  planName: string | undefined;
  workspaceId: string | undefined;
  entitlements: string[];
  planId: string;
}

/**
 * get plan information for the given workspaceId
 * @param workspaceId The ID of the workspace for which to fetch the plan information.
 * @returns The plan information for the specified workspace.
 */
export const usePlan = (workspaceId?: string): UseQueryResult<IPlanInfo> => {
  const { user } = useAuth();
  const wpId = workspaceId ?? user?.data?.workspaceId;
  const accessToken = getAccessToken();
  const query = useQuery({
    enabled: !!accessToken && !!wpId,
    refetchOnWindowFocus: "always",
    queryKey: ["user-plan", wpId],
    queryFn: async (): Promise<IPlanInfo> => {
      const client = new BaseClient(accessToken);
      const plans = await client.getPlansByWorkspaceId(wpId);
      const planInfo: IPlanInfo = getPlanInfo(plans.contracts ?? []);

      return planInfo;
    },
  });
  return query;
};

/**
 * get plan information for the given contracts
 * @param contracts
 * @returns plan information for the given contracts
 */
export const getPlanInfo = (contracts: ICropwiseBaseContract[]): IPlanInfo => {
  const AppId = process.env.REACT_APP_TRIALING_APP_ID ?? "";
  const plan = contracts.find(
    (p: ICropwiseBaseContract) =>
      isContractActive(p.start, p.end) && p.app_id === AppId
  );
  return {
    planName: plan?.plan_name,
    workspaceId: plan?.workspace_id,
    entitlements: plan?.entitlements?.map((k: { key: string }) => k.key) ?? [],
    planId: plan?.id ?? "",
  };
};
